'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renderRichText, storyblokEditable } from 'gatsby-source-storyblok';
import UtilityButton from '../buttons/utilityButton';
import FormInput from '../forms/form_input';
import FormCheckbox from '../forms/form_checkbox';
import { getSiteConfig } from '../../../siteConfig';
import { trackEvent } from '../../utils/tracking';

const NewsLetterSubscription = ({ content }) => {
  const { t } = useTranslation(['footer']);
  const [emailAddress, setEmailAddress] = useState();
  const [status, setStatus] = useState('disabled');
  const market = getSiteConfig().ethelMarket;

  const consents = [
    { market: 'SE', id: 'NEWSLETTER_WINTER_SE', title: 'Vinter' },
    {
      market: 'SE',
      id: 'NEWSLETTER_SUMMER_SE',
      title: 'Sommar- & vandringsresor',
    },
    { market: 'SE', id: 'NEWSLETTER_CONFERENCE_SE', title: 'Konferens' },
    { market: 'FI', id: 'NEWSLETTER_WINTER_FI', title: 'Hiihtomatkat' },
    {
      market: 'FI',
      id: 'NEWSLETTER_SUMMER_FI',
      title: 'Kesä- ja vaellusmatkat',
    },
    { market: 'NO', id: 'NEWSLETTER_WINTER_NO', title: 'Vinter' },
    {
      market: 'NO',
      id: 'NEWSLETTER_SUMMER_NO',
      title: 'Sommer- & vandringsreiser',
    },
  ];

  const airports = [
    {
      market: 'SE',
      id: 'AIRPORT_STOCKHOLM',
      title: 'Stockholm',
    },
    {
      market: 'SE',
      id: 'AIRPORT_GOTEBORG',
      title: 'Göteborg',
    },
    {
      market: 'SE',
      id: 'AIRPORT_MALMO',
      title: 'Malmö/Köpenhamn',
    },
  ];

  const [selectedConsents, setSelectedConsents] = useState([]);
  const [selectedAirports, setSelectedAirports] = useState(
    airports.filter((a) => a.market === market).map((a) => a.id),
  );

  const newsletterFormRef = useRef(null);
  const isValid =
    newsletterFormRef.current?.checkValidity() &&
    selectedConsents.length > 0 &&
    (market === 'SE' ? selectedAirports.length > 0 : true);
  const formSubmit = (e) => {
    e.preventDefault();
  };

  const newsLetterSubscribe = async (e) => {
    if (isValid) {
      setStatus('loading');

      const _consents = selectedConsents.map((c) => ({ name: c, status: 1 }));
      const contactData = {
        email: emailAddress,
        emailOptIn: 1,
        consents: _consents,
        market: market,
        tags: selectedAirports,
      };

      try {
        const response = await fetch('/.netlify/functions/newsletter', {
          method: 'POST',
          body: JSON.stringify(contactData),
        });

        if (response.ok) {
          setStatus('ok');
          trackEvent('newsletter_signup', {});
        } else {
          setStatus('error');
        }
      } catch (e) {
        setStatus('error');
      }
    }
  };

  const toggleConsent = (e) => {
    if (e.target.checked) {
      setSelectedConsents([...selectedConsents, e.target.name]);
    } else {
      setSelectedConsents(selectedConsents.filter((c) => c !== e.target.name));
    }
  };

  const toggleAirport = (e) => {
    if (e.target.checked) {
      setSelectedAirports([...selectedAirports, e.target.name]);
    } else {
      setSelectedAirports(selectedAirports.filter((c) => c !== e.target.name));
    }
  };

  const emailInputBlok = {
    label: '',
    type: 'email',
    name: 'email',
    required: true,
    placeholder: t('E-mail address'),
  };

  return (
    <div
      className="w-full rounded-t-2xl -mt-8 bg-yellow-30 py-12"
      {...storyblokEditable(content)}
    >
      <div className="w-full max-w-screen-xl mx-auto flex flex-col justify-between sm:flex-row px-2 md:px-8">
        <div
          className="w-full sm:w-1/2 sm:max-w-[30rem] px-4"
          dangerouslySetInnerHTML={{
            __html: renderRichText(content.newsletter_text),
          }}
        />
        {status === 'ok' && (
          <div
            className="w-full sm:w-1/2 md:pl-8"
            dangerouslySetInnerHTML={{
              __html: renderRichText(content.newsletter_thankyou),
            }}
          ></div>
        )}
        {status === 'error' && (
          <div
            className="w-full sm:w-1/2 md:pl-8"
            dangerouslySetInnerHTML={{
              __html: renderRichText(content.newsletter_error),
            }}
          ></div>
        )}
        {status != 'ok' && status != 'error' && (
          <div className="w-full sm:w-1/2">
            <form
              ref={newsletterFormRef}
              id="newsletter-form"
              onSubmit={formSubmit}
              className="sm:max-w-[30rem] "
            >
              <FormInput
                blok={emailInputBlok}
                handleInputChange={(e) => {
                  setEmailAddress(e.target.value);
                }}
              />
              <fieldset className="w-full flex py-1 flex-row max-w-lg flex-wrap">
                {consents
                  .filter((c) => c.market === market)
                  .map((c) => (
                    <FormCheckbox
                      handleInputChange={(e) => toggleConsent(e)}
                      blok={{
                        label: c.title,
                        name: c.id,
                        value: c.value,
                        required: false,
                        _uid: c.id,
                      }}
                      key={c.id}
                    />
                  ))}
              </fieldset>
              {market === 'SE' && (
                <>
                  <h4 className="text-xs px-4 md:px-8 pb-0">
                    Jag vill resa från:
                  </h4>
                  <fieldset className="w-full flex py-1 flex-row max-w-lg flex-wrap">
                    {airports
                      .filter((c) => c.market === market)
                      .map((c) => (
                        <FormCheckbox
                          handleInputChange={(e) => toggleAirport(e)}
                          blok={{
                            label: c.title,
                            name: c.id,
                            value: c.value,
                            required: false,
                            _uid: c.id,
                          }}
                          formValue={true}
                          key={c.id}
                        />
                      ))}
                  </fieldset>
                </>
              )}
              <div
                className="mb-4 mx-4 md:mx-8 text-xxs text-black-50"
                dangerouslySetInnerHTML={{
                  __html: renderRichText(content.newsletter_terms),
                }}
              ></div>
              <div className="mx-2 px-2 md:px-4">
                <UtilityButton
                  label={t('Subscribe')}
                  onClick={newsLetterSubscribe}
                  color={isValid ? 'yellow' : 'gray'}
                  enabled={isValid}
                />
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsLetterSubscription;
